import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MsgAction } from '../store/actions';

/**
 * Shared-media listing for a conversation's details panel: the first page when
 * the panel opens, further pages as it is scrolled.
 *
 * Both panes grew their own copy of this, and both copies had the same four
 * faults — the details panel could fire a burst of identical requests and then
 * sit on a spinner that never resolved:
 *
 * 1. Re-entrancy was guarded on the redux loading flag. Redux state lags the
 *    event stream by a render, and `scroll` fires many times per gesture, so
 *    every event in the first frame saw `loading === false` and dispatched.
 *    The latch is a ref here, so it is set before the next event is read.
 * 2. The offset lived in React state and was read from the handler's closure,
 *    so those same events all asked for the SAME page.
 * 3. The only stop condition was `loaded >= count`. When the server returns a
 *    short page — or reports a count it does not actually serve — `loaded`
 *    stops climbing while `count` stays ahead of it, and the guard can never
 *    hold: every scroll to the bottom re-requests a page that adds nothing. A
 *    short page now latches `exhausted` regardless of what `count` claims.
 * 4. Opening the panel always refetched page one, so toggling it re-requested
 *    data already in the store.
 *
 * The saga runs under `takeLatest`, which is what turned the burst into a stuck
 * spinner rather than merely wasted requests: each dispatch cancels the one
 * before it, and a cancelled saga puts neither SUCCESS nor FAILURE — so the
 * `SHARE_MEDIA` that set `share_media_loader: true` never had anything to set
 * it back.
 *
 * @param channelId  conversation to list media for
 * @param open       whether the details panel is on screen
 * @param pageSize   must match the `limit` MsgMiddleware.ShareMedia requests
 */
export const SHARED_MEDIA_PAGE = 10;

export const useSharedMedia = ({ channelId, open, pageSize = SHARED_MEDIA_PAGE }) => {
  const dispatch = useDispatch();

  // Selected field by field. The panes subscribe to the whole MsgReducer slice,
  // so anything reading through them re-renders on every unrelated write in it.
  const media = useSelector((state) => state?.MsgReducer?.share_media);
  const total = useSelector((state) => state?.MsgReducer?.share_media_count);
  const loadedCount = useSelector((state) => state?.MsgReducer?.share_media_length);
  const loading = useSelector((state) => state?.MsgReducer?.share_media_loader);

  /** A request is out. Set synchronously, so a burst of events sees it. */
  const pending = useRef(false);
  /** Offset the NEXT page starts at. */
  const nextOffset = useRef(0);
  /** The server has no more to give, whatever `count` reports. */
  const exhausted = useRef(false);
  /** Channel whose first page is already in the store. */
  const loadedChannel = useRef(null);

  // Declared before the effect that loads, so on a channel change the reset
  // runs first and the load below is what leaves its mark. Effects fire in
  // declaration order within a commit.
  useEffect(() => {
    loadedChannel.current = null;
    nextOffset.current = 0;
    exhausted.current = false;
    pending.current = false;
  }, [channelId]);

  const loadPage = useCallback(
    (offset) => {
      if (!channelId) return;
      pending.current = true;
      nextOffset.current = offset + pageSize;
      dispatch(MsgAction.ShareMedia({ channel_id: channelId, shareMediaOffset: offset }));
    },
    [channelId, dispatch, pageSize]
  );

  useEffect(() => {
    if (!open || !channelId) return;
    // Reopening the panel shows what is already held rather than re-fetching it.
    if (loadedChannel.current === channelId) return;

    loadedChannel.current = channelId;
    loadPage(0);
  }, [open, channelId, loadPage]);

  // Settle the latch when the request finishes, and decide whether that was the
  // last page. `loadedCount` short of where this page should have ended means
  // the server returned fewer rows than asked for — there is nothing after it.
  useEffect(() => {
    if (loading || !pending.current) return;

    pending.current = false;
    if ((loadedCount ?? 0) < nextOffset.current) exhausted.current = true;
  }, [loading, loadedCount]);

  const loadMore = useCallback(() => {
    if (pending.current || exhausted.current) return;

    const reported = Number(total) || 0;
    // Nothing to page through. This is the case the old code got wrong: with a
    // count of 0 its guard read `0 >= 0` and held, but the moment the server
    // reported a count it did not actually serve, the guard could never hold
    // and every scroll to the bottom fired another request.
    if (reported <= 0) return;
    if ((loadedCount ?? 0) >= reported) return;

    loadPage(nextOffset.current);
  }, [loadPage, loadedCount, total]);

  /** Hand straight to the details panel's scroll container. */
  const onScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      // Tolerance rather than `=== 0`: the difference is fractional on a
      // fractional-DPI display and the exact comparison never held there.
      if (scrollHeight - scrollTop - clientHeight > 4) return;
      loadMore();
    },
    [loadMore]
  );

  return {
    media,
    total,
    onScroll,
    // Only a spinner for a request that is actually out. `share_media_loader`
    // on its own is also true before anything has ever been asked for.
    loading: Boolean(loading) && pending.current,
  };
};

export default useSharedMedia;
